import { CommercialPlansMap, GeneratedPlansMap, SurgeonPlansMap } from '@lp/features/RoutePlanner/types';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';

export type SuggestedRoutesState = {
  currentTaskId: string;
  currentGeneratedRouteType: RouteTypeSettingsEnum | null;
  generatedDateTimeMs: number | null;
  isPlansLoaded: boolean;
  isSurgeonPlansLoaded: boolean;
  isCommercialsPlansLoaded: boolean;
  routePlans: GeneratedPlansMap;
  surgeonPlans: SurgeonPlansMap;
  commercialPlans: CommercialPlansMap;
  plansWarning: string | null;
  surgeonWarning: string | null;
};

export type SuggestedRoutesAction =
  | { type: 'reset' }
  | {
      type: 'plansLoaded';
      taskId: string;
      planType: RouteTypeSettingsEnum;
      plans: GeneratedPlansMap;
      generatedDateTimeMs: number;
      warning: string | null;
    }
  | { type: 'surgeonPlansUpdated'; plans: SurgeonPlansMap }
  | { type: 'surgeonPlansSettled'; warning?: string | null }
  | { type: 'commercialPlansUpdated'; plans: CommercialPlansMap }
  | { type: 'commercialPlansSettled' };

export const INITIAL_SUGGESTED_ROUTES_STATE: SuggestedRoutesState = {
  currentTaskId: '',
  currentGeneratedRouteType: null,
  generatedDateTimeMs: null,
  isPlansLoaded: false,
  isSurgeonPlansLoaded: false,
  isCommercialsPlansLoaded: false,
  routePlans: {},
  surgeonPlans: {},
  commercialPlans: {},
  plansWarning: null,
  surgeonWarning: null,
};

const mergeGrownPlans = <T>(current: Record<string, T[]>, incoming: Record<string, T[]>): Record<string, T[]> => {
  const grown = Object.entries(incoming).filter(([planId, plans]) => plans.length > (current[planId]?.length ?? 0));

  if (!grown.length) return current;

  return { ...current, ...Object.fromEntries(grown) };
};

export const suggestedRoutesReducer = (
  state: SuggestedRoutesState,
  action: SuggestedRoutesAction,
): SuggestedRoutesState => {
  switch (action.type) {
    case 'reset':
      return { ...INITIAL_SUGGESTED_ROUTES_STATE, generatedDateTimeMs: state.generatedDateTimeMs };
    case 'plansLoaded':
      return {
        ...state,
        currentTaskId: action.taskId,
        currentGeneratedRouteType: action.planType,
        generatedDateTimeMs: action.generatedDateTimeMs,
        routePlans: action.plans,
        plansWarning: action.warning,
        isPlansLoaded: true,
      };
    case 'surgeonPlansUpdated': {
      const surgeonPlans = mergeGrownPlans(state.surgeonPlans, action.plans);

      return surgeonPlans === state.surgeonPlans ? state : { ...state, surgeonPlans };
    }
    case 'surgeonPlansSettled':
      return state.isSurgeonPlansLoaded
        ? state
        : { ...state, isSurgeonPlansLoaded: true, surgeonWarning: action.warning ?? null };
    case 'commercialPlansUpdated': {
      const commercialPlans = mergeGrownPlans(state.commercialPlans, action.plans);

      return commercialPlans === state.commercialPlans ? state : { ...state, commercialPlans };
    }
    case 'commercialPlansSettled':
      return state.isCommercialsPlansLoaded ? state : { ...state, isCommercialsPlansLoaded: true };
    default:
      return state;
  }
};
